import {Injectable} from '@angular/core';
import {
  LOCAL_STORAGE_PREFIX, SESSION_STORAGE_PREFIX,
  StorageKeys,
  StorageNamespace,
  StorageValue
} from '../shared/types/storage';

interface IStorage<TInterface extends StorageNamespace> {
  setItem<K extends StorageKeys<TInterface>>(key: K, value: StorageValue<TInterface, K>): void;

  getItem<K extends StorageKeys<TInterface>>(key: K): StorageValue<TInterface, K> | undefined;

  removeItem<K extends StorageKeys<TInterface>>(key: K): void;

  hasItem<K extends StorageKeys<TInterface>>(key: K): boolean;

}

abstract class BaseStorageService<TInterface extends StorageNamespace>
  implements IStorage<TInterface> {

  protected abstract readonly storage: Storage;
  protected abstract readonly prefix: string;
  protected buildKey(key: StorageKeys<TInterface> ): string {
    return `${this.prefix}_${String(key)}`;
  }

  public setItem<K extends StorageKeys<TInterface>>(key: K, value: StorageValue<TInterface, K>): boolean {
    try {
      const fullKey = this.buildKey(key);
      const serialized = JSON.stringify(value);
      this.storage.setItem(fullKey, serialized);
      return true;
    } catch (error) {
      return false;
    }
  }

  public getItem<K extends StorageKeys<TInterface>>(key: K): StorageValue<TInterface, K> | undefined {
    try {
      const fullKey = this.buildKey(key);
      const item = this.storage.getItem(fullKey);
      if (item === null) {
        return undefined;
      }
      return JSON.parse(item) as StorageValue<TInterface, K>;
    } catch (error) {
      return undefined;
    }
  }


  public removeItem<K extends StorageKeys<TInterface>>(key: K): void {
    const fullKey = this.buildKey(key);
    this.storage.removeItem(fullKey);
  }

  public hasItem<K extends StorageKeys<TInterface>>(key: K): boolean {
    const fullKey = this.buildKey(key);
    return this.storage.getItem(fullKey) !== null;
  }

}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService
  extends BaseStorageService<'LOCAL'> {
  protected readonly storage = localStorage;
  protected readonly prefix = LOCAL_STORAGE_PREFIX;
}

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService
  extends BaseStorageService<'SESSION'> {

  protected readonly storage = sessionStorage;
  protected readonly prefix = SESSION_STORAGE_PREFIX;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  constructor(
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService
  ) {
  }

  get local() {
    return this.localStorage;
  }

  get session() {
    return this.sessionStorage;
  }

}
